import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { getBiggestContainer } from 'features/workload-optimization';

import { WorkloadDetailsContext } from '../../../workload-details/_context';
import { OptimizationContainer } from '@cast/types';

type WorkloadSelectedContainerState = {
  container?: OptimizationContainer;
  setContainerName: (name: string) => void;
  containers: OptimizationContainer[];
};

const WorkloadSelectedContainerContext =
  createContext<WorkloadSelectedContainerState | null>(null);

export const WorkloadSelectedContainerProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { workload, metrics } = useContext(WorkloadDetailsContext);
  const [selection, setSelection] = useState({
    workloadId: '',
    containerName: '',
  });

  // Merge containers from workload + unique containers from metrics
  const containers = useMemo(() => {
    if (!workload || !metrics) {
      return [];
    }

    // Start with all containers from workload
    const allContainers: OptimizationContainer[] = [...workload.containers];
    const workloadContainerNames = new Set(
      workload.containers.map((c) => c.name)
    );

    // Add containers from metrics that don't exist in workload
    if (metrics.containers) {
      metrics.containers.forEach((metricsContainer) => {
        if (!workloadContainerNames.has(metricsContainer.name)) {
          allContainers.push({ name: metricsContainer.name });
        }
      });
    }

    return allContainers;
  }, [workload, metrics]);

  // Selection only applies to the workload it was made on
  const containerName =
    selection.workloadId === workload?.id ? selection.containerName : '';

  // We can't save container to state because UI would show stale data, thus save container name and find container when data is refreshed
  const container = useMemo(
    () => containers.find((container) => container.name === containerName),
    [containerName, containers]
  );

  const setContainerName = useCallback(
    (name: string) =>
      setSelection({ workloadId: workload?.id ?? '', containerName: name }),
    [workload?.id]
  );

  useEffect(() => {
    if (!container && workload) {
      setSelection({
        workloadId: workload.id,
        containerName: getBiggestContainer(workload.containers)?.name ?? '',
      });
    }
  }, [container, workload]);

  return (
    <WorkloadSelectedContainerContext.Provider
      value={useMemo(
        () => ({ container, setContainerName, containers }),
        [container, containers, setContainerName]
      )}
    >
      {children}
    </WorkloadSelectedContainerContext.Provider>
  );
};

export const useWorkloadSelectedContainer = () => {
  const context = useContext(WorkloadSelectedContainerContext);

  if (!context) {
    throw new Error(
      'useWorkloadSelectedContainer must be used within a WorkloadSelectedContainerProvider'
    );
  }
  return context;
};
